import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Table as AntTable,
    Button as AntButton,
    Modal as AntModal,
    Form,
    Input,
    Select as AntSelect,
    Space,
    message,
    Popconfirm,
    Row,
    Col,
    Tag,
    Alert,
    Checkbox,
    Typography,
    Avatar,
    Upload
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    SyncOutlined,
    CloudDownloadOutlined,
    UserOutlined,
    UploadOutlined,
    PictureOutlined
} from '@ant-design/icons';
import Layout from '../components/Layout';

const { Text } = Typography;

const ROLE_OPTIONS = [
    { value: 1, label: 'Администратор' },
    { value: 2, label: 'Менеджер' },
    { value: 3, label: 'Исполнитель' },
    { value: 4, label: 'Заказчик' },
    { value: 5, label: 'Монтажник' },
    { value: 6, label: 'HR' },
    { value: 7, label: 'Управление пользователями' },
    { value: 8, label: 'Руководитель отдела персонала' }
];

const EXTRA_ROLE_OPTIONS = ROLE_OPTIONS.filter(
    r => ![4, 5].includes(r.value)
);

const PAGE_GRANT_OPTIONS = [
    { value: 'bitrix-tasks', label: 'Задачи Bitrix' },
    { value: 'tasks', label: 'Задачи (Gepard)' },
    { value: 'task-manager', label: 'Менеджер задач' },
    { value: 'hr-deals', label: 'Сделки HR' },
    { value: 'bitrix-analytics', label: 'HR-аналитика' },
    { value: 'invoices', label: 'ВДР' },
    { value: 'tmc', label: 'ТМЦ' },
    { value: 'users', label: 'Пользователи' }
];

const ACCESS_OPTIONS = [
    { value: 'view', label: 'Просмотр' },
    { value: 'edit', label: 'Редактирование' },
    { value: 'full', label: 'Полные' }
];

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [counterpartiesList, setCounterpartiesList] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [syncingAvatars, setSyncingAvatars] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [importReport, setImportReport] = useState(null);
    const [form] = Form.useForm();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchText, setSearchText] = useState('');

    const token = () => localStorage.getItem('token');
    const currentRoleId = (() => {
        try {
            return Number(
                JSON.parse(localStorage.getItem('user') || 'null')
                    ?.role_id
            );
        } catch {
            return null;
        }
    })();
    const isAdmin = currentRoleId === 1;
    const canManageUsers =
        currentRoleId === 1 || currentRoleId === 7;

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/users', {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    search: searchText
                },
                headers: { Authorization: `Bearer ${token()}` }
            });
            const data = Array.isArray(response.data)
                ? response.data
                : response.data.data || [];
            setUsers(data);
            const total = parseInt(
                response.headers['x-total-count'],
                10
            );
            if (!isNaN(total)) {
                setTotalUsers(total);
            } else {
                setTotalUsers(response.data.total || data.length);
            }
        } catch (error) {
            message.error('Ошибка при загрузке пользователей.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText]);

    const fetchCounterparties = useCallback(async () => {
        try {
            const response = await axios.get('/api/counterparties', {
                params: { limit: 2000 },
                headers: { Authorization: `Bearer ${token()}` }
            });
            const data = response.data.data || response.data || [];
            setCounterpartiesList(
                data.map(c => ({ label: c.name, value: c.id }))
            );
        } catch (error) {
            console.error('Ошибка загрузки контрагентов:', error);
        }
    }, []);

    const fetchOrgStructure = useCallback(async () => {
        try {
            const headers = { Authorization: `Bearer ${token()}` };
            const [depRes, posRes] = await Promise.all([
                axios.get('/api/departments', {
                    params: { activeOnly: 1 },
                    headers
                }),
                axios.get('/api/positions', {
                    params: { activeOnly: 1 },
                    headers
                })
            ]);
            setDepartments(depRes.data.data || []);
            setPositions(posRes.data.data || []);
        } catch (error) {
            console.error('Ошибка загрузки отделов/должностей:', error);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchCounterparties();
        fetchOrgStructure();
    }, [fetchUsers, fetchCounterparties, fetchOrgStructure]);

    const selectedDepartmentId = Form.useWatch(
        'department_id',
        form
    );

    const departmentOptions = useMemo(
        () =>
            departments.map(d => ({
                value: d.id,
                label: d.name
            })),
        [departments]
    );

    const positionOptions = useMemo(() => {
        const list = selectedDepartmentId
            ? positions.filter(
                  p =>
                      !p.department_id ||
                      Number(p.department_id) ===
                          Number(selectedDepartmentId)
              )
            : positions;
        return list.map(p => ({
            value: p.id,
            label: p.department?.name
                ? `${p.name} (${p.department.name})`
                : p.name
        }));
    }, [positions, selectedDepartmentId]);

    const handleSearch = value => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const openModal = (user = null) => {
        setEditingUser(user);
        setModalVisible(true);
        if (user) {
            form.setFieldsValue({
                ...user,
                role_id: user.role_id,
                role_ids: user.role_ids || [],
                bitrix_user_id: user.bitrix_user_id || null,
                counterparty_ids: user.counterparty_ids || [],
                department_id: user.department_id || null,
                position_id: user.position_id || null,
                see_new_tasks: Boolean(user.see_new_tasks),
                page_keys: [],
                page_access_level: 'edit'
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                role_id: 2,
                role_ids: [],
                counterparty_ids: [],
                department_id: null,
                position_id: null,
                see_new_tasks: false,
                page_keys: ['bitrix-tasks'],
                page_access_level: 'edit'
            });
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingUser(null);
        form.resetFields();
    };

    const handleFormSubmit = async values => {
        setLoading(true);
        try {
            const pageKeys = values.page_keys || [];
            const pageLevel = values.page_access_level || 'edit';
            const payload = {
                ...values,
                role_id: values.role_id?.value || values.role_id,
                role_ids: values.role_ids || [],
                bitrix_user_id: values.bitrix_user_id || null,
                department_id:
                    values.department_id === undefined ||
                    values.department_id === null ||
                    values.department_id === ''
                        ? null
                        : Number(values.department_id),
                position_id:
                    values.position_id === undefined ||
                    values.position_id === null ||
                    values.position_id === ''
                        ? null
                        : Number(values.position_id),
                external_id:
                    values.external_id === ''
                        ? null
                        : values.external_id,
                page_grants: pageKeys.map(page_key => ({
                    page_key,
                    access_level: pageLevel
                }))
            };
            delete payload.page_keys;
            delete payload.page_access_level;

            if (editingUser) {
                if (!payload.password) delete payload.password;
                await axios.put(
                    `/api/users/${editingUser.id}`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token()}`
                        }
                    }
                );
                message.success('Пользователь успешно обновлен!');
            } else {
                await axios.post('/api/users', payload, {
                    headers: {
                        Authorization: `Bearer ${token()}`
                    }
                });
                message.success('Пользователь успешно создан!');
            }
            closeModal();
            setTimeout(() => fetchUsers(), 100);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Ошибка сохранения пользователя.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async userId => {
        setLoading(true);
        try {
            await axios.delete(`/api/users/${userId}`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            message.success('Пользователь успешно удален!');
            fetchUsers();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Ошибка удаления пользователя.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleImportBitrix = async () => {
        if (!isAdmin) {
            message.warning('Импорт доступен только администратору');
            return;
        }
        setImporting(true);
        try {
            const { data } = await axios.post(
                '/api/users/import-bitrix',
                {
                    defaultRoleId: 2,
                    grantBitrixTasks: true,
                    bitrixTasksLevel: 'edit'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token()}`
                    }
                }
            );
            setImportReport(data.report || null);
            const org = data.report?.org;
            const orgHint = org?.departments
                ? `, отделов ${org.departments.upserted || 0}, привязок ${org.links?.department_set || 0}`
                : '';
            message.success(
                `Импорт: создано ${data.report?.created?.length || 0}, связано ${data.report?.linked?.length || 0}${orgHint}`
            );
            fetchUsers();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Ошибка импорта из Bitrix'
            );
        } finally {
            setImporting(false);
        }
    };

    const handleSyncAvatars = async () => {
        if (!canManageUsers) {
            message.warning(
                'Синхронизация доступна администратору'
            );
            return;
        }
        setSyncingAvatars(true);
        try {
            const { data } = await axios.post(
                '/api/users/sync-avatars-bitrix',
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token()}`
                    }
                }
            );
            const r = data?.report || {};
            message.success(
                `Аватары: обновлено ${r.updated || 0}, ` +
                    `пропущено ${r.skipped || 0}, ` +
                    `ошибок ${r.failed || 0}`
            );
            fetchUsers();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось синхронизировать аватарки'
            );
        } finally {
            setSyncingAvatars(false);
        }
    };

    const handleUploadAvatar = async file => {
        if (!editingUser?.id) {
            message.warning(
                'Сначала сохраните пользователя'
            );
            return false;
        }
        setUploadingAvatar(true);
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            const { data } = await axios.post(
                `/api/users/${editingUser.id}/avatar`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token()}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setEditingUser(prev =>
                prev
                    ? {
                          ...prev,
                          avatar_url: data.avatar_url
                      }
                    : prev
            );
            message.success('Аватар обновлён');
            fetchUsers();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Ошибка загрузки аватара'
            );
        } finally {
            setUploadingAvatar(false);
        }
        return false;
    };

    const userColumns = useMemo(
        () => [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
            {
                title: '',
                dataIndex: 'avatar_url',
                key: 'avatar',
                width: 56,
                render: (url, record) => (
                    <Avatar
                        size={36}
                        src={url || undefined}
                        icon={<UserOutlined />}
                        style={{
                            backgroundColor: '#1890ff'
                        }}
                    >
                        {(
                            record.first_name ||
                            record.login ||
                            '?'
                        )
                            .toString()
                            .charAt(0)
                            .toUpperCase()}
                    </Avatar>
                )
            },
            {
                title: 'Bitrix',
                dataIndex: 'bitrix_user_id',
                key: 'bitrix_user_id',
                width: 90,
                render: id => (id ? <Tag color="purple">{id}</Tag> : '—')
            },
            {
                title: 'Фамилия',
                dataIndex: 'last_name',
                key: 'last_name',
                width: 120
            },
            {
                title: 'Имя',
                dataIndex: 'first_name',
                key: 'first_name',
                width: 120
            },
            {
                title: 'Отдел',
                key: 'department',
                width: 140,
                render: (_, row) =>
                    row.department?.name || '—'
            },
            {
                title: 'Должность',
                key: 'position',
                width: 140,
                render: (_, row) =>
                    row.position?.name || '—'
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                key: 'phone',
                width: 120
            },
            {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                width: 180
            },
            {
                title: 'Логин',
                dataIndex: 'login',
                key: 'login',
                width: 120
            },
            {
                title: 'Новые заявки',
                dataIndex: 'see_new_tasks',
                key: 'see_new_tasks',
                width: 130,
                render: value =>
                    value ? <Tag color="green">Видит</Tag> : <span style={{ color: '#ccc' }}>—</span>
            },
            {
                title: 'Роли',
                key: 'roles',
                width: 220,
                render: (_, record) => {
                    const ids = [
                        record.role_id,
                        ...(record.role_ids || [])
                    ].filter(Boolean);
                    return (
                        <>
                            {ids.map(roleId => {
                                const role = ROLE_OPTIONS.find(
                                    r => r.value === roleId
                                );
                                return (
                                    <Tag
                                        key={roleId}
                                        color={
                                            roleId ===
                                            record.role_id
                                                ? 'blue'
                                                : 'cyan'
                                        }
                                        style={{ marginBottom: 4 }}
                                    >
                                        {role?.label || roleId}
                                        {roleId ===
                                        record.role_id
                                            ? ' ★'
                                            : ''}
                                    </Tag>
                                );
                            })}
                        </>
                    );
                }
            },
            {
                title: 'Контрагенты',
                key: 'counterparties',
                width: 180,
                render: (_, record) =>
                    record.counterparties_details?.length ? (
                        record.counterparties_details.map(cp => (
                            <Tag
                                key={cp.id}
                                color="orange"
                                style={{ marginBottom: 4 }}
                            >
                                {cp.name}
                            </Tag>
                        ))
                    ) : (
                        <span style={{ color: '#ccc' }}>—</span>
                    )
            },
            {
                title: 'Действия',
                key: 'actions',
                width: 120,
                fixed: 'right',
                render: (_, record) => (
                    <Space size="small">
                        <AntButton
                            icon={<EditOutlined />}
                            onClick={() => openModal(record)}
                            type="primary"
                            size="small"
                        />
                        <Popconfirm
                            title="Удалить пользователя?"
                            onConfirm={() =>
                                handleDelete(record.id)
                            }
                            okText="Да"
                            cancelText="Нет"
                        >
                            <AntButton
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Popconfirm>
                    </Space>
                )
            }
        ],
        []
    );

    return (
        <Layout>
            <div
                style={{
                    padding: '20px',
                    width: '95%',
                    margin: '0 auto'
                }}
            >
                <h1 style={{ marginBottom: '20px' }}>
                    Управление пользователями
                </h1>

                {importReport ? (
                    <Alert
                        style={{ marginBottom: 16 }}
                        type="success"
                        closable
                        onClose={() => setImportReport(null)}
                        message={`Bitrix: создано ${importReport.created?.length || 0}, связано ${importReport.linked?.length || 0}, право «Задачи Bitrix» выдано ${importReport.granted_bitrix_tasks?.length || 0}`}
                        description={
                            importReport.created?.some(
                                c => c.temporary_password
                            ) ? (
                                <div>
                                    <Text strong>
                                        Временные пароли новых
                                        пользователей:
                                    </Text>
                                    <ul style={{ marginBottom: 0 }}>
                                        {importReport.created
                                            .filter(
                                                c =>
                                                    c.temporary_password
                                            )
                                            .map(c => (
                                                <li key={c.user_id}>
                                                    {c.login} /{' '}
                                                    {
                                                        c.temporary_password
                                                    }{' '}
                                                    ({c.email})
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            ) : (
                                'Существующие пользователи связаны по email и получили доступ к Задачам Bitrix.'
                            )
                        }
                    />
                ) : null}

                <div
                    style={{
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Input.Search
                                placeholder="Поиск по имени, email, логину…"
                                value={searchText}
                                onChange={e =>
                                    setSearchText(e.target.value)
                                }
                                onSearch={handleSearch}
                                enterButton={
                                    <AntButton
                                        icon={<SearchOutlined />}
                                    >
                                        Поиск
                                    </AntButton>
                                }
                            />
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Space wrap>
                                {isAdmin ? (
                                    <AntButton
                                        icon={
                                            <CloudDownloadOutlined />
                                        }
                                        loading={importing}
                                        onClick={handleImportBitrix}
                                    >
                                        Импорт из Bitrix
                                    </AntButton>
                                ) : null}
                                {canManageUsers ? (
                                    <AntButton
                                        icon={
                                            <PictureOutlined />
                                        }
                                        loading={syncingAvatars}
                                        onClick={
                                            handleSyncAvatars
                                        }
                                    >
                                        Аватары из Bitrix
                                    </AntButton>
                                ) : null}
                                <AntButton
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => openModal()}
                                >
                                    Добавить пользователя
                                </AntButton>
                                <AntButton
                                    icon={<SyncOutlined />}
                                    onClick={() => {
                                        setSearchText('');
                                        setCurrentPage(1);
                                        fetchUsers();
                                    }}
                                >
                                    Обновить
                                </AntButton>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <AntTable
                    columns={userColumns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalUsers,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            if (pageSize !== size)
                                setPageSize(size);
                        }
                    }}
                    bordered
                    scroll={{ x: 1400 }}
                />

                <AntModal
                    title={
                        editingUser
                            ? 'Редактировать пользователя'
                            : 'Добавить пользователя'
                    }
                    open={modalVisible}
                    onCancel={closeModal}
                    footer={null}
                    width={760}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFormSubmit}
                    >
                        {editingUser ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 16,
                                    marginBottom: 16
                                }}
                            >
                                <Avatar
                                    size={72}
                                    src={
                                        editingUser.avatar_url ||
                                        undefined
                                    }
                                    icon={<UserOutlined />}
                                    style={{
                                        backgroundColor:
                                            '#1890ff'
                                    }}
                                />
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={
                                        handleUploadAvatar
                                    }
                                >
                                    <AntButton
                                        icon={
                                            <UploadOutlined />
                                        }
                                        loading={uploadingAvatar}
                                    >
                                        Загрузить аватар
                                    </AntButton>
                                </Upload>
                            </div>
                        ) : null}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="last_name"
                                    label="Фамилия"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Введите фамилию!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="first_name"
                                    label="Имя"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Введите имя!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="middle_name"
                                    label="Отчество"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="phone"
                                    label="Телефон"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                'Введите телефон!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Введите Email!'
                                        },
                                        {
                                            type: 'email',
                                            message:
                                                'Введите корректный Email!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="login"
                                    label="Логин"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Введите логин!'
                                        }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="password"
                                    label="Пароль"
                                    rules={
                                        editingUser
                                            ? []
                                            : [
                                                  {
                                                      required: true,
                                                      message:
                                                          'Введите пароль!'
                                                  }
                                              ]
                                    }
                                >
                                    <Input.Password
                                        placeholder={
                                            editingUser
                                                ? 'Оставьте пустым, чтобы не менять'
                                                : 'Введите пароль'
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="bitrix_user_id"
                                    label="ID пользователя Bitrix"
                                >
                                    <Input
                                        type="number"
                                        placeholder="например 4970"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="role_id"
                            label="Основная роль"
                            rules={[
                                {
                                    required: true,
                                    message: 'Выберите роль!'
                                }
                            ]}
                        >
                            <AntSelect
                                placeholder="Выберите роль"
                                options={ROLE_OPTIONS}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="department_id"
                                    label="Отдел"
                                >
                                    <AntSelect
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Выберите отдел"
                                        options={departmentOptions}
                                        onChange={() => {
                                            form.setFieldsValue({
                                                position_id: null
                                            });
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="position_id"
                                    label="Должность"
                                >
                                    <AntSelect
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Выберите должность"
                                        options={positionOptions}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="role_ids"
                            label="Дополнительные роли"
                            extra="Можно выбрать несколько. Права страниц суммируются (берётся максимальный уровень)."
                        >
                            <AntSelect
                                mode="multiple"
                                allowClear
                                placeholder="Доп. роли (например HR + Менеджер)"
                                options={EXTRA_ROLE_OPTIONS}
                                optionFilterProp="label"
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={16}>
                                <Form.Item
                                    name="page_keys"
                                    label="Доп. права на страницы"
                                    extra="Персональные права поверх ролей. Для тонкой настройки — раздел «Права»."
                                >
                                    <AntSelect
                                        mode="multiple"
                                        allowClear
                                        options={PAGE_GRANT_OPTIONS}
                                        optionFilterProp="label"
                                        placeholder="Выберите страницы"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="page_access_level"
                                    label="Уровень прав"
                                >
                                    <AntSelect
                                        options={ACCESS_OPTIONS}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="see_new_tasks"
                            valuePropName="checked"
                            extra="Во вкладке «Можно взять» в приложении исполнителя этот сотрудник видит ещё заявки со статусом «Новое». Остальным видны только «Назначено на организацию»."
                        >
                            <Checkbox>Видит новые заявки</Checkbox>
                        </Form.Item>

                        <Form.Item
                            name="counterparty_ids"
                            label="Связанные контрагенты"
                        >
                            <AntSelect
                                mode="multiple"
                                placeholder="Выберите контрагентов"
                                options={counterpartiesList}
                                optionFilterProp="label"
                                allowClear
                                showSearch
                            />
                        </Form.Item>

                        <Form.Item
                            name="external_id"
                            label="Внешний ID"
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item>
                            <Space
                                style={{
                                    width: '100%',
                                    justifyContent: 'flex-end'
                                }}
                            >
                                <AntButton onClick={closeModal}>
                                    Отмена
                                </AntButton>
                                <AntButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                >
                                    Сохранить
                                </AntButton>
                            </Space>
                        </Form.Item>
                    </Form>
                </AntModal>
            </div>
        </Layout>
    );
};

export default UsersPage;
